import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Bold, ImagePlus, Link2, List, Loader2, Pencil, Trash2 } from "lucide-react";
import { API } from "@/data/campaign";
import { isVideoUrl } from "@/utils/media";

const inputCls =
  "w-full border border-pine-700/25 bg-paper px-4 py-3.5 text-base text-ink placeholder:text-ink/40 focus:border-terra-500 focus:outline-none";
const labelCls = "mb-1.5 block font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-ink/60";

const EMPTY = { title: "", body: "", excerpt: "", image_url: "", published: false };

const AdminUpdates = ({ headers }) => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const bodyRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/campaign-updates`, { headers });
      setPosts(data.items);
    } catch {
      toast.error("Could not load campaign updates.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const wrapSelection = (before, after = before) => {
    const el = bodyRef.current;
    if (!el) return;
    const { selectionStart: s, selectionEnd: e, value } = el;
    const sel = value.slice(s, e) || "text";
    const next = value.slice(0, s) + before + sel + after + value.slice(e);
    setForm((f) => ({ ...f, body: next }));
  };

  const insertLink = () => {
    const url = window.prompt("Link URL (https://…)");
    if (!url) return;
    const el = bodyRef.current;
    const { selectionStart: s, selectionEnd: e, value } = el;
    const sel = value.slice(s, e) || "link text";
    setForm((f) => ({ ...f, body: value.slice(0, s) + `<a href="${url}">` + sel + "</a>" + value.slice(e) }));
  };

  const makeList = () => {
    const el = bodyRef.current;
    const { selectionStart: s, selectionEnd: e, value } = el;
    const sel = value.slice(s, e) || "First point\nSecond point";
    const items = sel.split("\n").filter(Boolean).map((l) => `<li>${l}</li>`).join("");
    setForm((f) => ({ ...f, body: value.slice(0, s) + `<ul>${items}</ul>` + value.slice(e) }));
  };

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/admin/campaign-updates/upload`, fd, { headers });
      setForm((f) => ({ ...f, image_url: data.url }));
      toast.success(isVideoUrl(data.url) ? "Video uploaded." : "Photo uploaded.");
    } catch {
      toast.error("Upload failed — photos under 5MB (jpg, png, webp, gif), videos under 30MB (mp4, webm, mov).");
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    if (!form.title.trim()) {
      toast.error("Title is required.");
      return;
    }
    setSaving(true);
    try {
      if (editId) {
        await axios.patch(`${API}/admin/campaign-updates/${editId}`, form, { headers });
        toast.success("Post updated.");
      } else {
        await axios.post(`${API}/admin/campaign-updates`, form, { headers });
        toast.success(form.published ? "Post is live on /news." : "Draft saved.");
      }
      setForm(EMPTY);
      setEditId(null);
      load();
    } catch {
      toast.error("Could not save the post.");
    } finally {
      setSaving(false);
    }
  };

  const edit = (p) => {
    setEditId(p.id);
    setForm({ title: p.title, body: p.body || "", excerpt: p.excerpt || "", image_url: p.image_url || "", published: p.published });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const remove = async (id) => {
    await axios.delete(`${API}/admin/campaign-updates/${id}`, { headers });
    toast.success("Post deleted.");
    load();
  };

  return (
    <div data-testid="campaign-updates-editor">
      <div className="border border-pine-700/15 bg-white/60 p-6 sm:p-8">
        <h3 className="font-serif text-xl font-medium tracking-tight">{editId ? "Edit post" : "New campaign update"}</h3>
        <div className="mt-5 space-y-4">
          <label className="block">
            <span className={labelCls}>Title *</span>
            <input data-testid="update-title-input" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} className={inputCls} placeholder="Post title" />
          </label>
          <div>
            <span className={labelCls}>Body</span>
            <div className="mb-2 flex gap-2">
              <button type="button" data-testid="update-bold-btn" onClick={() => wrapSelection("<strong>", "</strong>")} className="flex h-9 w-9 items-center justify-center border border-pine-700/25 hover:border-terra-500" aria-label="Bold"><Bold className="h-4 w-4" /></button>
              <button type="button" data-testid="update-link-btn" onClick={insertLink} className="flex h-9 w-9 items-center justify-center border border-pine-700/25 hover:border-terra-500" aria-label="Add link"><Link2 className="h-4 w-4" /></button>
              <button type="button" data-testid="update-list-btn" onClick={makeList} className="flex h-9 w-9 items-center justify-center border border-pine-700/25 hover:border-terra-500" aria-label="Make list"><List className="h-4 w-4" /></button>
            </div>
            <textarea ref={bodyRef} rows={8} data-testid="update-body-input" value={form.body} onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))} className={inputCls} placeholder="Write the update… Use the toolbar for bold, links, and lists. Blank lines become paragraphs." />
          </div>
          <div>
            <span className={labelCls}>Photo or video (optional)</span>
            <div className="flex flex-wrap items-center gap-4">
              <label className="flex cursor-pointer items-center gap-2 border border-pine-700/25 px-4 py-2.5 text-sm font-semibold text-pine-700 transition-colors hover:border-terra-500 hover:text-terra-500">
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}
                {uploading ? "Uploading…" : "Upload photo or video"}
                <input type="file" accept="image/*,video/*" data-testid="update-photo-input" onChange={uploadPhoto} className="hidden" />
              </label>
              {form.image_url && (
                isVideoUrl(form.image_url) ? (
                  <video src={form.image_url} controls playsInline preload="metadata" data-testid="update-video-preview" className="h-16 w-28 border border-pine-700/15 object-cover" />
                ) : (
                  <img src={form.image_url} alt="post" className="h-16 w-28 border border-pine-700/15 object-cover" />
                )
              )}
            </div>
          </div>
          <label className="block">
            <span className={labelCls}>Excerpt (optional — auto-written from body if blank)</span>
            <input data-testid="update-excerpt-input" value={form.excerpt} onChange={(e) => setForm((f) => ({ ...f, excerpt: e.target.value }))} className={inputCls} placeholder="One-sentence summary" />
          </label>
          <label className="flex cursor-pointer items-center gap-3 border border-pine-700/20 bg-paper p-4">
            <input type="checkbox" data-testid="update-published-checkbox" checked={form.published} onChange={(e) => setForm((f) => ({ ...f, published: e.target.checked }))} className="h-5 w-5 accent-pine-700" />
            <span className="text-sm font-medium text-ink/80">Published — visible on /news (uncheck to unpublish)</span>
          </label>
          <div className="flex flex-wrap gap-3">
            <button onClick={save} disabled={saving} data-testid="update-save-button" className="flex items-center gap-2 bg-pine-700 px-7 py-3.5 text-sm font-semibold text-paper transition-colors hover:bg-terra-500 disabled:opacity-60">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              {editId ? "Save changes" : form.published ? "Publish post" : "Save draft"}
            </button>
            {editId && (
              <button onClick={() => { setEditId(null); setForm(EMPTY); }} data-testid="update-cancel-edit" className="border border-pine-700/25 px-6 py-3.5 text-sm font-semibold text-pine-700 hover:border-terra-500">
                Cancel edit
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="mt-8 space-y-3" data-testid="updates-list">
        {loading ? (
          <div className="flex items-center gap-2 py-10 text-ink/50"><Loader2 className="h-5 w-5 animate-spin" /> Loading…</div>
        ) : posts.length === 0 ? (
          <p className="border border-dashed border-pine-700/25 py-10 text-center text-sm text-ink/50">No campaign updates yet. Write your first one above.</p>
        ) : (
          posts.map((p) => (
            <div key={p.id} data-testid={`update-row-${p.id}`} className="flex flex-wrap items-center justify-between gap-3 border border-pine-700/15 bg-white/60 px-6 py-4">
              <div className="min-w-0">
                <div className="flex items-center gap-3">
                  <span className={`px-2.5 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.15em] ${p.published ? "bg-pine-700 text-paper" : "bg-parch text-ink/60"}`}>
                    {p.published ? "Live" : "Draft"}
                  </span>
                  <p className="truncate font-serif text-lg font-semibold tracking-tight">{p.title}</p>
                </div>
                <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.15em] text-ink/45">
                  {new Date(p.published_at || p.created_at).toLocaleString()}
                </p>
              </div>
              <div className="flex gap-2">
                <button onClick={() => edit(p)} data-testid={`update-edit-${p.id}`} className="flex h-9 w-9 items-center justify-center border border-pine-700/25 text-pine-700 hover:border-pine-700 hover:bg-pine-700 hover:text-paper" aria-label="Edit">
                  <Pencil className="h-4 w-4" />
                </button>
                <button onClick={() => remove(p.id)} data-testid={`update-delete-${p.id}`} className="flex h-9 w-9 items-center justify-center border border-pine-700/25 text-pine-700 hover:border-red-600 hover:bg-red-600 hover:text-white" aria-label="Delete">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default AdminUpdates;
